import { MASTER_RAMP_SEC } from "../audio/constants";
import type { DisposeRegistry } from "../audio/DisposeRegistry";
import type { GainLike, ToneAdapter } from "../audio/tone";
import type { MasterMuteReason } from "../state/types";

/** Limiter ceiling. Required by the spec — the master must never clip. */
const LIMITER_THRESHOLD_DB = -1;

/**
 * Master chain and the applied-gain policy (docs/implementation-plan.md 3.13).
 *
 * The UI volume value and the applied output gain are separate:
 * while paused or panicked the output gain stays at 0 even if the user moves the
 * slider, because up to (dispatch window + lookAhead) of audio may already be
 * reserved in Web Audio and would otherwise become audible again.
 */
export class MasterBus {
  private readonly input: GainLike;
  private appliedGain = 0;
  private muteReason: MasterMuteReason = "starting";

  constructor(tone: ToneAdapter, disposeRegistry: DisposeRegistry) {
    const chain = tone.createMasterChain(LIMITER_THRESHOLD_DB);
    this.input = chain.input;
    for (const node of chain.nodes) {
      disposeRegistry.register(node);
    }
  }

  /** Raises the output to the given volume. Only init() and resume() call this. */
  unmute(volume: number, rampSec: number = MASTER_RAMP_SEC): void {
    this.muteReason = "none";
    this.appliedGain = volume;
    this.input.gain.rampTo(volume, rampSec);
  }

  /** Drops the output to 0 and records why. */
  mute(reason: Exclude<MasterMuteReason, "none">, rampSec: number = MASTER_RAMP_SEC): void {
    this.muteReason = reason;
    this.appliedGain = 0;
    this.input.gain.rampTo(0, rampSec);
  }

  /**
   * Applies a UI volume change. Ignored (value kept, gain untouched) unless the
   * engine is currently audible.
   */
  applyVolumeIfAudible(volume: number): void {
    if (this.muteReason !== "none") return;
    this.appliedGain = volume;
    this.input.gain.rampTo(volume, MASTER_RAMP_SEC);
  }

  get gain(): number {
    return this.appliedGain;
  }

  get reason(): MasterMuteReason {
    return this.muteReason;
  }
}
